// Client-side CV → text extraction (PDF via unpdf, DOCX via mammoth).
// Same libraries the seller onboarding CV import uses. Dynamic imports keep them out of the
// SSR bundle and only load them in the browser when a file is actually picked.

const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const MIN_CHARS = 50;

/** Client-side extraction failure. `copyKey` is a `copies.json` key under `cv_match.*`. */
export class CvParseError extends Error {
    constructor(public readonly copyKey: string) {
        super(copyKey);
        this.name = 'CvParseError';
    }
}

export async function extractCvText(file: File): Promise<string> {
    if (file.size > MAX_BYTES) {
        throw new CvParseError('cv_match.error_file_too_large');
    }

    const name = file.name.toLowerCase();
    const buffer = await file.arrayBuffer();

    let text: string;
    if (name.endsWith('.pdf') || file.type === 'application/pdf') {
        const { getDocumentProxy, extractText } = await import('unpdf');
        const pdf = await getDocumentProxy(new Uint8Array(buffer));
        const result = await extractText(pdf, { mergePages: true });
        text = Array.isArray(result.text) ? result.text.join('\n') : result.text;
    } else if (name.endsWith('.docx') || file.type.includes('officedocument.wordprocessingml')) {
        const mammoth = (await import('mammoth')).default;
        const result = await mammoth.extractRawText({ arrayBuffer: buffer });
        text = result.value;
    } else {
        throw new CvParseError('cv_match.error_unsupported_file');
    }

    const trimmed = text.trim();
    if (trimmed.length < MIN_CHARS) {
        throw new CvParseError('cv_match.error_unreadable_file');
    }
    return trimmed;
}
